import apiClient from './apiClient';

// Types matching the backend API response
export interface Host {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  type: 'individual' | 'fleet';
  status: 'approved' | 'pending' | 'rejected';
  contact: {
    email: string;
    phone: string;
    profilePicture?: string;
  };
  branding: {
    companyName: string | null;
    logoUrl: string | null;
  };
  assets: {
    vehiclesCount: number;
    approvedVehicles: number;
    activeVehicles: number;
    driversCount: number;
    yachtsCount: number;
  };
  performance: {
    totalBookings: number;
    totalEarnings: number;
  };
  dates: {
    joinedAt: string;
    approvedAt?: string;
  };
}

export interface HostPayout {
  id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  host: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    totalEarnings: number;
    availableBalance: number;
    totalWithdrawn: number;
  };
  bankAccount: {
    accountNumber: string;
    bankName: string;
    accountName: string;
  };
  paystackReference?: string;
  requestedAt: string;
  completedAt?: string;
  approvedBy?: string;
}

export interface HostsListResponse {
  success: boolean;
  data: {
    hosts: Host[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
}

export interface HostPayoutsResponse {
  success: boolean;
  data: {
    payouts: HostPayout[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    stats: {
      pending: {
        count: number;
        totalAmount: number;
      };
      completed: {
        count: number;
        totalAmount: number;
      };
      failed: {
        count: number;
        totalAmount: number;
      };
    };
  };
}

export interface HostEarningsFilters {
  type?: 'all' | 'individual' | 'fleet';
  status?: 'all' | 'approved' | 'pending' | 'rejected';
  limit?: number;
  offset?: number;
  search?: string;
  sortBy?: 'earnings' | 'name' | 'bookings' | 'joinedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface HostPayoutFilters {
  page?: number;
  limit?: number;
  status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  hostId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

// Detailed Host Earnings Types
export interface HostDetailedEarnings {
  host: {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    type: 'individual' | 'fleet_manager';
    partnerTier: 'standard' | 'premium' | 'fleet';
    totalEarnings: number;
    availableBalance: number;
    totalWithdrawn: number;
  };
  summary: {
    totalEarnings: number;
    platformFee: number;
    commissionVat: number;
    netEarnings: number;
    platformFeeRate: number;
    platformFeePercentage: number;
    commissionVatPercentage: number;
    partnerTier: string;
    pendingEarnings: number;
    completedBookings: number;
    pendingBookings: number;
    avgBookingValue: number;
    currentWeekEarnings: number;
    lastWeekEarnings: number;
    weeklyGrowth: number;
    currentMonthEarnings: number;
    lastMonthEarnings: number;
    monthlyGrowth: number;
    currentYearEarnings: number;
    currency: string;
  };
  earningsByVehicle: Array<{
    vehicleId: string;
    vehicleName: string;
    make: string;
    model: string;
    year: string;
    plateNumber: string;
    image: string | null;
    totalEarnings: number;
    completedBookings: number;
    activeBookings: number;
    averagePerBooking: number;
    averageRating: number;
    utilizationRate: number;
    utilizationBreakdown: {
      daysListed: number;
      daysBlocked: number;
      daysBooked: number;
      daysAvailable: number;
    };
  }>;
  earningsByYacht?: Array<{
    yachtId: string;
    yachtName: string;
    totalEarnings: number;
    completedBookings: number;
    averageRating: number;
    utilizationRate: number;
  }>;
  earningsByDay?: Array<{
    date: string;
    earnings: number;
    completedBookings: number;
  }>;
  earningsByMonth?: Array<{
    month: string;
    earnings: number;
    completedBookings: number;
  }>;
  recentTransactions?: Array<{
    bookingId: string;
    bookingNumber: string;
    vehicleName: string;
    renterName: string;
    amount: number;
    startDate: string;
    endDate: string;
    completedAt: string;
  }>;
  filters?: {
    period: string;
    vehicleId: string | null;
    yachtId: string | null;
    startDate: string | null;
    endDate: string | null;
  };
}

export interface HostDetailedEarningsFilters {
  period?: 'all' | 'today' | 'week' | 'month' | 'year';
  vehicleId?: string;
  yachtId?: string;
  startDate?: string;
  endDate?: string;
}

class HostEarningsService {
  /**
   * Get all hosts with their earnings summary
   */
  async getHosts(filters?: HostEarningsFilters): Promise<HostsListResponse> {
    try {
      const params = new URLSearchParams();

      if (filters?.type && filters.type !== 'all') params.append('type', filters.type);
      if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await apiClient.get(
        `/api/v1/admin/hosts?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching hosts:', error);
      throw error;
    }
  }

  /**
   * Get host payout requests with filtering
   */
  async getHostPayouts(filters?: HostPayoutFilters): Promise<HostPayoutsResponse> {
    try {
      const params = new URLSearchParams();

      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.status) params.append('status', filters.status);
      if (filters?.hostId) params.append('hostId', filters.hostId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.search) params.append('search', filters.search);

      const response = await apiClient.get(
        `/api/v1/admin/host-payouts?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching host payouts:', error);
      throw error;
    }
  }

  /**
   * Approve a pending payout
   */
  async approvePayout(payoutId: string, adminNotes?: string): Promise<void> {
    try {
      await apiClient.post(
        `/api/v1/admin/host-payouts/${payoutId}/approve`,
        { adminNotes }
      );
    } catch (error) {
      console.error('Error approving payout:', error);
      throw error;
    }
  }

  /**
   * Reject a pending payout
   */
  async rejectPayout(payoutId: string, reason: string): Promise<void> {
    try {
      await apiClient.post(
        `/api/v1/admin/host-payouts/${payoutId}/reject`,
        { reason }
      );
    } catch (error) {
      console.error('Error rejecting payout:', error);
      throw error;
    }
  }

  /**
   * Get detailed earnings breakdown for a specific host
   */
  async getHostDetailedEarnings(
    hostId: string,
    filters?: HostDetailedEarningsFilters
  ): Promise<{ success: boolean; data: HostDetailedEarnings; timestamp: string }> {
    try {
      const params = new URLSearchParams();

      if (filters?.period) params.append('period', filters.period);
      if (filters?.vehicleId) params.append('vehicleId', filters.vehicleId);
      if (filters?.yachtId) params.append('yachtId', filters.yachtId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await apiClient.get(
        `/api/v1/admin/hosts/${hostId}/earnings?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching host detailed earnings:', error);
      throw error;
    }
  }

  /**
   * Calculate summary statistics from hosts data
   */
  async getEarningsSummary() {
    try {
      // Fetch all approved hosts
      const response = await this.getHosts({ status: 'approved', limit: 1000 });
      const hosts = response.data.hosts;

      // Calculate totals
      const totalEarnings = hosts.reduce((sum, host) => sum + host.performance.totalEarnings, 0);
      const totalHosts = hosts.length;
      const activeHosts = hosts.filter(h => h.assets.activeVehicles > 0).length;
      const averageEarningsPerHost = totalHosts > 0 ? totalEarnings / totalHosts : 0;

      // Get payout stats (optional - may fail if endpoint structure differs)
      let stats = {
        pending: { count: 0, totalAmount: 0 },
        completed: { count: 0, totalAmount: 0 },
      };

      try {
        const payoutsResponse = await this.getHostPayouts({ limit: 1 });
        console.log('Host payouts stats response:', payoutsResponse);

        if (payoutsResponse.data?.stats) {
          stats = {
            pending: payoutsResponse.data.stats.pending || { count: 0, totalAmount: 0 },
            completed: payoutsResponse.data.stats.completed || { count: 0, totalAmount: 0 },
          };
          console.log('Parsed stats:', stats);
        } else {
          console.warn('No stats in payouts response:', payoutsResponse);
        }
      } catch (error) {
        console.error('Host payouts stats fetch failed:', error);
      }

      // Get top 10 earning hosts and fetch their detailed earnings
      const topHosts = [...hosts]
        .sort((a, b) => b.performance.totalEarnings - a.performance.totalEarnings)
        .slice(0, 10);

      // Fetch detailed earnings for top hosts to get accurate available balances
      const topEarningHostsPromises = topHosts.map(async (host) => {
        try {
          const detailedEarnings = await this.getHostDetailedEarnings(host.id);
          return {
            hostId: host.id,
            hostName: host.name,
            hostEmail: host.contact.email,
            hostCode: host.id.substring(0, 8),
            totalEarnings: detailedEarnings.data.host.totalEarnings,
            availableBalance: detailedEarnings.data.host.availableBalance,
            pendingEarnings: detailedEarnings.data.summary.pendingEarnings || 0,
            completedPayouts: 0,
            pendingPayouts: 0,
            bookingCount: host.performance.totalBookings,
            lastPayoutDate: undefined,
            status: host.status === 'approved' ? 'active' as const : 'inactive' as const,
          };
        } catch (error) {
          console.error(`Failed to fetch detailed earnings for host ${host.id}:`, error);
          // Fallback to basic data if detailed earnings fetch fails
          return {
            hostId: host.id,
            hostName: host.name,
            hostEmail: host.contact.email,
            hostCode: host.id.substring(0, 8),
            totalEarnings: host.performance.totalEarnings,
            availableBalance: 0,
            pendingEarnings: 0,
            completedPayouts: 0,
            pendingPayouts: 0,
            bookingCount: host.performance.totalBookings,
            lastPayoutDate: undefined,
            status: host.status === 'approved' ? 'active' as const : 'inactive' as const,
          };
        }
      });

      const topEarningHosts = await Promise.all(topEarningHostsPromises);

      return {
        totalEarnings,
        totalHosts,
        activeHosts,
        pendingPayouts: stats.pending.totalAmount,
        completedPayouts: stats.completed.totalAmount,
        averageEarningsPerHost,
        topEarningHosts,
        earningsTrend: [],
      };
    } catch (error) {
      console.error('Error calculating earnings summary:', error);
      throw error;
    }
  }

  /**
   * Export host earnings to CSV
   */
  async exportHostEarnings(filters?: HostEarningsFilters): Promise<Blob> {
    try {
      const response = await this.getHosts(filters);
      const hosts = response.data.hosts;

      // Create CSV content
      const headers = ['Host Name', 'Email', 'Type', 'Status', 'Total Earnings', 'Total Bookings', 'Vehicles', 'Joined Date'];
      const rows = hosts.map(host => [
        host.name,
        host.contact.email,
        host.type,
        host.status,
        host.performance.totalEarnings.toFixed(2),
        host.performance.totalBookings,
        host.assets.vehiclesCount,
        new Date(host.dates.joinedAt).toLocaleDateString(),
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(',')),
      ].join('\n');

      return new Blob([csvContent], { type: 'text/csv' });
    } catch (error) {
      console.error('Error exporting host earnings:', error);
      throw error;
    }
  }
}

export const hostEarningsService = new HostEarningsService();
